/**
 * Email Tag Picker Controller — client-side filter, toggle + create tags.
 *
 * Replaces original email_tag_picker_controller.js.
 * All logic is unchanged except:
 *   - Uses proper response.json() (the original had a subtle bug: `await response.json`
 *     without () just returns the method reference; fixed here to `await response.json()`)
 *   - Uses contrastColor helper for WCAG-compliant badge text
 */

import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'

export default class extends Controller {
  static targets  = ['search', 'list', 'newTagName', 'newTagColor']
  static values   = { emailId: Number }

  // ── Client-side filter ────────────────────────────────────────────────────

  filter() {
    const query = this.searchTarget.value.toLowerCase().trim()
    this.listTarget.querySelectorAll('.email-tag-picker-item').forEach(item => {
      const tagName = (item.dataset.tagName || '').toLowerCase()
      item.style.display = tagName.includes(query) ? '' : 'none'
    })
  }

  // ── Toggle tag on/off ─────────────────────────────────────────────────────

  async toggleTag(e) {
    const tagId = e.params.tagId
    await post('/emails/tags/toggle', {
      body: JSON.stringify({ email_id: this.emailIdValue, tag_id: tagId }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  // ── Create new tag ────────────────────────────────────────────────────────

  async createTag() {
    const name  = this.newTagNameTarget.value.trim()
    if (!name) return

    const color    = this.newTagColorTarget.value
    const response = await post('/emails/tags', {
      body: JSON.stringify({ email_tag: { tag: name, color } }),
      contentType: 'application/json',
      responseKind: 'json'
    })

    if (!response.ok) return

    // Fix: call json() as a function (the original had `response.json` without parens)
    const tag = await response.json()
    this.newTagNameTarget.value = ''

    // Construct the new tag item and append it immediately (no Turbo Stream needed)
    const label = document.createElement('label')
    label.className    = 'dropdown-item d-flex align-items-center gap-2 small rounded py-1 px-2 email-tag-picker-item'
    label.dataset.tagName = (tag.tag || '').toLowerCase()

    const checkbox = document.createElement('input')
    checkbox.type      = 'checkbox'
    checkbox.className = 'form-check-input mt-0'
    checkbox.dataset.action = 'change->email-tag-picker#toggleTag'
    checkbox.dataset.emailTagPickerTagIdParam = tag.id

    const badge = document.createElement('span')
    badge.className = 'badge email-tag-badge rounded-pill'
    if (tag.color) {
      badge.style.backgroundColor = tag.color
      badge.style.color           = this._contrastColor(tag.color)
    }
    badge.textContent = tag.tag

    label.appendChild(checkbox)
    label.appendChild(badge)
    this.listTarget.appendChild(label)
  }

  // ── WCAG contrast helper ──────────────────────────────────────────────────

  /**
   * Returns '#333' or '#fff' based on W3C relative luminance of the hex color.
   * Ensures readable text on any tag badge background.
   */
  _contrastColor(hex) {
    const h = hex.replace('#', '')
    const r = parseInt(h.substring(0, 2), 16)
    const g = parseInt(h.substring(2, 4), 16)
    const b = parseInt(h.substring(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255.0
    return luminance > 0.55 ? '#333' : '#fff'
  }
}
